import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { api, publicApi, hasSession } from '../../lib/api'
import { usePageMeta } from '../../hooks/usePageMeta'
import { cameFromApp, APP_RETURN_URL_SIGNED_IN, APP_RETURN_URL_GUEST } from '../../lib/appHandoff'
import { Loader2, CheckCircle, Clock } from 'lucide-react'

/**
 * Serves both donor types returning from Stripe Checkout at `/donate/return`.
 * A signed-in donor still gets the existing write-path verify (which logs the
 * donation); a guest gets the new read-only summary. If the write-path verify
 * fails for a signed-in donor (eg. a stale session), we fall through to the
 * public summary rather than showing an error, since the donation may still
 * have gone through as a guest-style checkout.
 */
export default function DonationComplete() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')

  const [donation, setDonation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Whether this donor gave without an account — drives the account-creation
  // nudge, which makes no sense for someone who is already signed in.
  const [isGuest, setIsGuest] = useState(false)

  // Tracks the sessionId that verification has already been started (or
  // completed) for. `api.verifyCheckoutSession` (the authed branch of
  // `load()` below) is a write — it logs the donation — so it must not fire
  // twice for the same session_id, including across React StrictMode's
  // dev-mode mount -> unmount -> remount cycle. Keyed by sessionId (rather
  // than a plain boolean) so a genuine navigation to a different
  // `session_id` still triggers a fresh verification.
  const verificationRef = useRef(null)

  usePageMeta({ title: 'Thank You — Claritas', description: 'Your donation was received.' })

  useEffect(() => {
    if (!sessionId) {
      setError("We couldn't find that donation.")
      setLoading(false)
      return
    }

    // Guard against StrictMode's dev-mode double-invocation of effects (and
    // any other re-run for the same session_id): `api.verifyCheckoutSession`
    // below is a write, not an idempotent read, so it must fire at most once
    // per sessionId. Set the ref *before* the network call, not just before
    // the resulting state update, so a synchronous mount -> unmount ->
    // remount can't sneak a second call in ahead of the first one setting it.
    if (verificationRef.current === sessionId) {
      return
    }
    verificationRef.current = sessionId

    async function load() {
      try {
        if (await hasSession()) {
          try {
            const logged = await api.verifyCheckoutSession(sessionId)
            // The verify response is the donation row, which carries only the
            // parish id; look the name up so the thank-you names the parish.
            let parishName = logged.parishName
            if (!parishName && logged.parish_id) {
              try {
                parishName = (await api.getParish(logged.parish_id))?.name
              } catch {
                // Keep the generic wording; the gift itself is confirmed.
              }
            }
            setDonation({
              amount: logged.amount,
              parishId: logged.parish_id,
              parishName: parishName ?? 'your parish',
              emailMasked: null,
              status: logged.status ?? 'succeeded',
              customFields: logged.customFields ?? [],
            })
            setIsGuest(false)
            return
          } catch (e) {
            // Fall through to the public summary below.
          }
        }

        const summary = await publicApi.verifyGuestDonation(sessionId)
        setDonation(summary)
        setIsGuest(true)
      } catch (e) {
        setError("We couldn't find that donation.")
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [sessionId])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error || !donation) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <h1 className="text-2xl font-bold font-display text-charcoal mb-2">
          {error || "We couldn't find that donation."}
        </h1>
        <Link to="/parishes" className="text-gold font-semibold">
          Browse parishes &rarr;
        </Link>
      </div>
    )
  }

  // The verify endpoints report 'succeeded' | 'pending'. A payment that hasn't
  // settled yet must not be confirmed as though it had.
  const isSettled = donation.status === undefined || donation.status === 'succeeded'

  return (
    <div className="max-w-2xl mx-auto py-20 px-4 text-center">
      {isSettled ? (
        <CheckCircle className="w-12 h-12 text-gold mx-auto mb-6" />
      ) : (
        <Clock className="w-12 h-12 text-charcoal-muted mx-auto mb-6" />
      )}
      <h1 className="text-3xl font-bold font-display text-charcoal mb-3">
        {isSettled ? 'Thank you for your gift' : "We're still confirming your gift"}
      </h1>
      <p className="text-lg text-charcoal-muted mb-2">
        ${(donation.amount / 100).toFixed(2)}
        {donation.parishName ? ` to ${donation.parishName}` : ''}
      </p>
      {!isSettled && (
        <p className="text-sm text-charcoal-muted mb-2">
          Your payment is still processing. We'll confirm it as soon as it clears
          — there's nothing more you need to do.
        </p>
      )}
      {donation.emailMasked && (
        <p className="text-sm text-charcoal-muted mb-8">
          A confirmation email is on its way to {donation.emailMasked}
        </p>
      )}
      {donation.customFields && donation.customFields.length > 0 && (
        <div
          data-testid="donation-custom-fields"
          className="bg-white border border-surface-border rounded shadow-card p-6 text-left mb-8"
        >
          <dl>
            {donation.customFields.map((field, index) => (
              <div
                key={field.fieldId ?? `${field.label}-${index}`}
                className={index > 0 ? 'mt-3' : ''}
              >
                <dt className="text-sm font-semibold text-charcoal">{field.label}</dt>
                <dd className="text-sm text-charcoal-muted">{field.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {isGuest && (
      <div className="bg-white border border-surface-border rounded shadow-card p-6 text-left">
        <h2 className="font-bold font-display text-charcoal mb-2">
          Want to track your giving?
        </h2>
        <p className="text-sm text-charcoal-muted mb-4">
          Create a Claritas account to see your donation history, set up recurring
          gifts, and round up your everyday purchases for your parish.
        </p>
        <Link to="/sign-in" className="text-gold font-semibold">
          Create an account &rarr;
        </Link>
      </div>
      )}
      {cameFromApp() && (
        <p className="mt-8">
          <a
            href={isGuest ? APP_RETURN_URL_GUEST : APP_RETURN_URL_SIGNED_IN}
            className="text-gold font-semibold"
          >
            Return to the Claritas app
          </a>
        </p>
      )}
      {donation.parishId && (
        <p className="mt-8">
          <Link to={`/parishes/${donation.parishId}`} className="text-charcoal-muted">
            Back to {donation.parishName || 'the parish page'}
          </Link>
        </p>
      )}
    </div>
  )
}
